import {
  ItemRecipientType,
  ItemSubjectType,
  ResponseSubject,
  ResponseSubjectType,
  User,
} from "../../types";

/**
 * Determines whether a form item is applicable for a given *subject* in the
 * results view, based only on the subject's role (team lead / instructor).
 *
 * Unlike `isCompatible`, this function intentionally ignores the respondent's
 * (author's) role and recipient-type restrictions — those are relevant when
 * deciding who fills out a form, not when displaying results for a subject.
 *
 * @param responseSubjectType - The mode being viewed (Peer, General, TeamInstructors, …)
 * @param itemSubjectType - The item's subject-type field
 * @param isSubjectTeamLead - Whether the subject being viewed is a team lead
 * @param isSubjectInstructor - Whether the subject being viewed is an instructor
 */
export function isSubjectCompatible(
  responseSubjectType: ResponseSubjectType,
  itemSubjectType: ItemSubjectType,
  isSubjectTeamLead: boolean,
  isSubjectInstructor: boolean,
): boolean {
  // General view: only show General items
  if (responseSubjectType === ResponseSubjectType.General) {
    return itemSubjectType === ItemSubjectType.General;
  }

  // TeamInstructors view: only show TeamInstructors items
  if (responseSubjectType === ResponseSubjectType.TeamInstructors) {
    return itemSubjectType === ItemSubjectType.TeamInstructors;
  }

  // Peer view: filter by the subject's role
  switch (itemSubjectType) {
    case ItemSubjectType.PeerSelf:
    case ItemSubjectType.PeerOnly:
    case ItemSubjectType.TeamLearners:
      // These are for non-instructor subjects only
      return !isSubjectInstructor;
    case ItemSubjectType.LeadSelf:
    case ItemSubjectType.LeadOnly:
      // These are for team-lead subjects only (and not instructors)
      return isSubjectTeamLead && !isSubjectInstructor;
    case ItemSubjectType.NonLeadSelf:
    case ItemSubjectType.NonLeadOnly:
      // These are for non-lead, non-instructor subjects only
      return !isSubjectTeamLead && !isSubjectInstructor;
    case ItemSubjectType.TeamInstructors:
      // In peer view, TeamInstructors items are for instructor subjects
      return isSubjectInstructor;
    default:
      return false;
  }
}

export default function isCompatible(
  responseSubjectType: ResponseSubjectType,
  itemSubjectType: ItemSubjectType,
  itemRecipientType?: ItemRecipientType,
  author?: User,
  isAuthorTeamLead?: boolean,
  responseSubject?: ResponseSubject,
): boolean {
  // General feedback is compatible with general questions, but still subject to
  // recipient-type filtering so instructors don't see student-only items and
  // vice-versa (same rules as the peer/points tab).
  if (responseSubjectType === ResponseSubjectType.General) {
    if (itemSubjectType !== ItemSubjectType.General) {
      return false;
    }
    const isAuthorInstructor = author?.isInstructor === true;
    switch (itemRecipientType) {
      case ItemRecipientType.TeamLeaders:
        return isAuthorTeamLead === true;
      case ItemRecipientType.NonLeaders:
        return isAuthorTeamLead !== true && !isAuthorInstructor;
      case ItemRecipientType.TeamInstructors:
        return isAuthorInstructor;
      case ItemRecipientType.TeamMembers:
      case ItemRecipientType.TeamLearners:
      default:
        return !isAuthorInstructor;
    }
  }

  // Instructor results "Team instructor ratings" view: show team-instructor questions
  if (responseSubjectType === ResponseSubjectType.TeamInstructors) {
    return itemSubjectType === ItemSubjectType.TeamInstructors;
  }

  if (!responseSubject || !itemRecipientType) {
    switch (itemSubjectType) {
      case ItemSubjectType.PeerSelf:
      case ItemSubjectType.PeerOnly:
      case ItemSubjectType.LeadSelf:
      case ItemSubjectType.LeadOnly:
      case ItemSubjectType.NonLeadSelf:
      case ItemSubjectType.NonLeadOnly:
        return true;
      case ItemSubjectType.TeamInstructors:
        // For TeamInstructors, we need the responseSubject to check the subject user's role
        // If we don't have the responseSubject, we can't determine compatibility
        return false;
      case ItemSubjectType.TeamLearners:
        // TeamLearners has been consolidated into PeerSelf
        // Treat it the same as PeerSelf for backward compatibility
        return true;
      default:
        return false;
    }
  }

  const isLead = responseSubject.isTeamLead;
  const isSelf = responseSubject.subjectUserId === author?.id;
  const isAuthorInstructor = author?.isInstructor === true;

  switch (itemRecipientType) {
    case ItemRecipientType.TeamLeaders:
      if (!isAuthorTeamLead) {
        return false;
      }
      break;
    case ItemRecipientType.NonLeaders:
      if (isAuthorTeamLead) {
        return false;
      }
      // NonLeaders targets non-lead students only; instructors must also be excluded
      if (isAuthorInstructor) {
        return false;
      }
      break;
    case ItemRecipientType.TeamInstructors:
      // Only show to instructors - exclude if author is not an instructor
      if (!isAuthorInstructor) {
        return false;
      }
      break;
    case ItemRecipientType.TeamMembers:
      // Team members questions are for students (learners) only
      // Exclude if author is an instructor (instructors should use TeamInstructors questions)
      if (isAuthorInstructor) {
        return false;
      }
      break;
    case ItemRecipientType.TeamLearners:
      // TeamLearners has been consolidated into TeamMembers
      // Treat it the same as TeamMembers for backward compatibility
      if (isAuthorInstructor) {
        return false;
      }
      break;
    default:
      break;
  }

  switch (itemSubjectType) {
    case ItemSubjectType.PeerSelf:
      // Only show peer/self questions if the subject user is NOT an instructor
      // Instructors should only see TEAM_INSTRUCTORS_QUESTION questions
      return responseSubject.subjectUserIsInstructor !== true;
    case ItemSubjectType.PeerOnly:
      // Only show peer-only questions if the subject user is NOT an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return !isSelf;
    case ItemSubjectType.LeadSelf:
      // Only show lead self-evaluation questions to leads evaluating themselves
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return isLead;
    case ItemSubjectType.LeadOnly:
      // Only show lead evaluation questions to non-leads evaluating leads
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return isLead && !isSelf;
    case ItemSubjectType.NonLeadSelf:
      // Only show non-lead self-evaluation questions to non-leads evaluating themselves
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return !isLead;
    case ItemSubjectType.NonLeadOnly:
      // Only show non-lead evaluation questions to leads/non-leads evaluating non-leads
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return !isLead && !isSelf;
    case ItemSubjectType.TeamInstructors:
      // Only show instructor evaluation questions when the subject user is an instructor
      if (responseSubjectType !== ResponseSubjectType.Peer) {
        return false;
      }
      // Check if subject user is an instructor using the embedded field
      return responseSubject.subjectUserIsInstructor === true;
    case ItemSubjectType.TeamLearners:
      // TeamLearners has been consolidated into PeerSelf
      // Treat it the same as PeerSelf for backward compatibility
      return true;
    default:
      return false;
  }
}
